import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, Button, Divider, Grid } from "@material-ui/core";
import { CollapsiblePanel } from "src/apps/commons/dataDisplays";
import DateTimeTextField from "src/apps/commons/inputs/components/DateTimeTextField";
import InputThreeStateRadio from "src/apps/commons/inputs/components/InputThreeStateRadio";
import { parseToOffsetDateTime } from "src/utils/dateUtils";
import { RemyndBrandFilterTypes } from "../types";

type FormData = {
  hasImage?: boolean;
  hasContact?: boolean;
  hasPhoneNumber?: boolean;
  hasEmail?: boolean;
  hasCoordinate?: boolean;
  hasProduct?: boolean;
  active?: boolean;
  createdAtMin?: OffsetDateTime;
  createdAtMax?: OffsetDateTime;
  updatedAtMin?: OffsetDateTime;
  updatedAtMax?: OffsetDateTime;
};

interface Props {
  onSubmit: (filter: RemyndBrandFilterTypes) => void;
}

const ListFilterPanel: React.FC<Props> = props => {
  const { onSubmit } = props;

  const defaultValues = useMemo<PartialNull<FormData>>(() => ({}), []);
  const { handleSubmit, control, reset } = useForm<PartialNull<FormData>>({
    defaultValues: defaultValues
  });

  const handleTransformAndSubmit = (form: FormData) => {
    const {
      createdAtMin,
      createdAtMax,
      updatedAtMin,
      updatedAtMax,
      ...restForm
    } = form;
    onSubmit({
      ...restForm,
      createdAtMin: createdAtMin
        ? parseToOffsetDateTime(createdAtMin)
        : undefined,
      createdAtMax: createdAtMax
        ? parseToOffsetDateTime(createdAtMax, { sec: 59, ms: 999 })
        : undefined,
      updatedAtMin: updatedAtMin
        ? parseToOffsetDateTime(updatedAtMin)
        : undefined,
      updatedAtMax: updatedAtMax
        ? parseToOffsetDateTime(updatedAtMax, { sec: 59, ms: 999 })
        : undefined
    });
  };

  return (
    <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
      <CollapsiblePanel title="Search Filters" icon="filter" defaultCollapse>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Controller
              name="createdAtMin"
              control={control}
              defaultValue={defaultValues?.createdAtMin ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="สร้าง ตั้งแต่"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="createdAtMax"
              control={control}
              defaultValue={defaultValues?.createdAtMax ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="สร้าง ไม่เกิน"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="updatedAtMin"
              control={control}
              defaultValue={defaultValues?.updatedAtMin ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="แก้ไขล่าสุด ตั้งแต่"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="updatedAtMax"
              control={control}
              defaultValue={defaultValues?.updatedAtMax ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="แก้ไขล่าสุด ไม่เกิน"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} />

          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="hasImage"
                control={control}
                defaultValue={defaultValues.hasImage}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="มีรูปโลโก้"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="hasContact"
                control={control}
                defaultValue={defaultValues.hasContact}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="มีข้อมูล Contact"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="active"
                control={control}
                defaultValue={defaultValues.active}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="Active"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider light />
          </Grid>

          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="hasPhoneNumber"
                control={control}
                defaultValue={defaultValues.hasPhoneNumber}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="มีเบอร์โทร"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="hasEmail"
                control={control}
                defaultValue={defaultValues.hasEmail}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="มี Email"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="hasCoordinate"
                control={control}
                defaultValue={defaultValues.hasCoordinate}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="มี Latitude & Longitude"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider light />
          </Grid>

          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="hasProduct"
                control={control}
                defaultValue={defaultValues.hasProduct}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="มีข้อมูลสินค้าในระบบ"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12} />

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginRight: 8 }}
            >
              Apply Filters
            </Button>
            <Button
              variant="contained"
              color="default"
              type="button"
              onClick={() => {
                reset();
                handleTransformAndSubmit({});
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </CollapsiblePanel>
    </form>
  );
};

export default ListFilterPanel;
